import { Link } from "react-router-dom";
import { useScrollReveal } from "@/hooks/useScrollReveal";

export default function AboutSection() {
  const { ref: ref1, isVisible: v1 } = useScrollReveal<HTMLDivElement>(0.15);
  const { ref: ref2, isVisible: v2 } = useScrollReveal<HTMLDivElement>(0.15, 150);

  return (
    <section id="about" className="bg-white py-20 md:py-28">
      <div className="w-full px-4 md:px-8 max-w-6xl mx-auto">
        <div className="text-center mb-16">
          <span className="text-gold text-sm tracking-[4px] font-bold block mb-2">ABOUT</span>
          <h2 className="text-2xl md:text-3xl font-bold text-navy">私たちについて</h2>
          <div className="w-16 h-1 bg-gold mx-auto mt-4 rounded-full" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 items-start">
          <div className="lg:col-span-3 space-y-10">
            <div
              ref={ref1}
              className={`transition-all duration-700 ${v1 ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
            >
              <h3 className="text-xl md:text-2xl font-bold text-navy leading-relaxed mb-5">
                大阪の発展を支える、誇りある警備
              </h3>
              <p className="text-base md:text-lg text-gray-700 leading-loose">
                私たちは、大阪府公安委員会より正式な認定を受けた警備会社。平日であれば、街のインフラを支える<strong>土木・通信・ガス工事などの警備</strong>を行います。それに加え、私たちはあえて専門性が求められる2つの分野に力を入れています。それが<span className="text-navy font-bold">【高速道路】</span>と<span className="text-navy font-bold">【イベント】</span>の警備。高速道路は社会インフラを守るプライドを、イベントは多くの来場者の安全を守るやりがいを感じられます。私たちは、やりがいと収入の両方を実現できる環境を目指しています。
              </p>
            </div>

            <div
              ref={ref2}
              className={`transition-all duration-700 ${v2 ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
            >
              <h3 className="text-xl md:text-2xl font-bold text-navy leading-relaxed mb-5">
                規律と優しさで、業界を変えていく。
              </h3>
              <p className="text-base md:text-lg text-gray-700 leading-loose">
                利他主義を企業理念としている当社。警備業界の長く受け継がれてきた慣習を変え、誰もが誇りを持って働ける"ニュースタンダード"をつくること。そのために、仲間との団結力、インフラを守るプライド、そして働く人を守るやさしさを大切にしています。
              </p>
            </div>
          </div>

          <div className="lg:col-span-2 flex items-center justify-center">
            <div className="w-full bg-gray-50 rounded-2xl p-6 md:p-8 border border-gray-100">
              <div className="text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gold/10 flex items-center justify-center">
                  <span className="w-8 h-8 flex items-center justify-center text-gold">
                    <i className="ri-shield-star-line text-2xl"></i>
                  </span>
                </div>
                <h4 className="text-lg font-bold text-navy mb-2">企業理念</h4>
                <p className="text-sm text-gray-600 leading-relaxed mb-4">
                  "利他主義"<br />
                  他者の利益を第一に考え、<br />
                  社会に貢献する
                </p>
                <div className="w-12 h-0.5 bg-gold mx-auto mb-4" />
                <p className="text-sm text-gray-600 leading-relaxed">
                  誰もが誇りを持って働ける<br />
                  警備業界のニュースタンダードをつくる
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-16 md:mt-20 space-y-10">
          <div className="bg-gray-50 rounded-3xl overflow-hidden">
            <div className="grid grid-cols-1 md:grid-cols-2">
              <div className="h-64 md:h-auto">
                <img
                  src="/images/GRANSTARK-OSAKA_office1_president's seat.jpeg"
                  alt="GRANSTARK代表・三田晃士のオフィス"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
              <div className="p-8 md:p-12 flex flex-col justify-center">
                <span className="text-navy text-sm tracking-[3px] font-bold mb-2">MESSAGE</span>
                <h3 className="text-xl md:text-2xl font-bold text-navy mb-4">メッセージ</h3>
                <p className="text-base text-gray-700 leading-relaxed mb-6">
                  警備の未来も、生活も、守るー。代表の三田より、警備業界で働く魅力についてメッセージです。
                </p>
                <Link
                  to="/recruit#message"
                  className="inline-flex items-center gap-2 border border-[#1A0DAB] text-[#1A0DAB] font-bold text-sm px-6 py-3 rounded-lg transition-all duration-300 hover:bg-[#1A0DAB] hover:text-white w-fit whitespace-nowrap"
                >
                  Read More
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-right-line"></i>
                  </span>
                </Link>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-3xl overflow-hidden border border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-2">
              <div className="p-8 md:p-12 flex flex-col justify-center order-2 md:order-1">
                <span className="text-navy text-sm tracking-[3px] font-bold mb-2">BUSINESS</span>
                <h3 className="text-xl md:text-2xl font-bold text-navy mb-4">事業内容</h3>
                <p className="text-base text-gray-700 leading-relaxed mb-6">
                  大阪の発展を支える、誇りある警備ー。専門性の高い"2つの柱"と多様な警備サービスについてご紹介します。
                </p>
                <Link
                  to="/business"
                  className="inline-flex items-center gap-2 border border-[#1A0DAB] text-[#1A0DAB] font-bold text-sm px-6 py-3 rounded-lg transition-all duration-300 hover:bg-[#1A0DAB] hover:text-white w-fit whitespace-nowrap"
                >
                  Read More
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-right-line"></i>
                  </span>
                </Link>
              </div>
              <div className="h-64 md:h-auto order-1 md:order-2">
                <img
                  src="/images/equipment.jpeg"
                  alt="警備スタッフの装備品"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}